import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { NatService } from 'src/app/services/nat.service';
import { ToastrService } from 'ngx-toastr';
import { ImportWizardService } from '@/app/pages/project/map/repository/import-wizard/import-wizard.service';
import { ImportWizardGeoSpatialService } from '@/app/pages/project/map/repository/import-wizard/components/geospatial/import-wizard-geospatial.service';
import { ImportWizardStorage, TabularColumns, ValidationErrorsInterface } from '@/app/pages/project/map/repository/import-wizard/import-wizard.storage';

export type NatsResponseInterface = {
    status: string;
    rows_validated: number;
    total_rows: number;
    progress_percentage: number;
    errors_found: number;
    valid: boolean;
};

export interface ValidationStartOptions {
    importId: string;
    columns: TabularColumns[];
    page: number;
    pageSize: number;
}

@Injectable()
export class ImportWizardGeoSpatialValidationHandler implements OnDestroy {

    private readonly _progress$ = new Subject<number>();
    private readonly _subs = new Subscription();

    readonly progress$: Observable<number> = this._progress$.asObservable();

    constructor(
        private readonly natsService: NatService,
        private readonly importWizardService: ImportWizardService,
        private readonly importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private readonly importWizardStorage: ImportWizardStorage,
        private readonly toastrService: ToastrService,
    ) {}

    /**
     * Deve ser chamado no ngOnInit do componente.
     * Subscreve o NATS imediatamente para não perder mensagens.
     */
    init(importId: string, page: number, pageSize: number): void {
        this._subscribeNats(importId, page, pageSize);
    }

    /**
     * Deve ser chamado ao acionar a validação.
     * O NATS já está ouvindo desde o init().
     */
    start({ importId, columns, page, pageSize }: ValidationStartOptions): void {
        const sub = this.importWizardGeoSpatialService
            .postValidateImportWizard(importId)
            .subscribe({
                next: () => {
                    this.importWizardStorage.setDisplayColumns(columns);
                },
                error: (err) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao iniciar validação:', err);
                },
            });

        this._subs.add(sub);
    }

    ngOnDestroy(): void {
        this._progress$.complete();
        this._subs.unsubscribe();
    }

    // ─── Private ────────────────────────────────────────────────────────────────

    private _subscribeNats(importId: string, page: number, pageSize: number): void {
        const topic = `import_wizard.${importId}.validation`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleNatsMessage(msg, importId, page, pageSize);
        });
    }

    private _handleNatsMessage(msg: string, importId: string, page: number, pageSize: number): void {
        try {
            const result: NatsResponseInterface = JSON.parse(msg);
            this._progress$.next(result.progress_percentage);

            if (result.progress_percentage === 100) {
                result.valid
                    ? this._handleSuccess()
                    : this._handleValidationErrors(importId, page, pageSize);
            }
        } catch (error) {
            console.error('[GeoSpatialValidationHandler] Erro ao processar mensagem NATS:', error);
        }
    }

    private _handleSuccess(): void {
        this.toastrService.success('Importação validada com sucesso!');
        
        this.importWizardStorage.setNextStep();
    }

    private _handleValidationErrors(importId: string, page: number, pageSize: number): void {
        const sub = this.importWizardService
            .getImportWizardErrors(importId, page, pageSize)
            .subscribe({
                next: (data) => {
                    this.importWizardStorage.setValidationErrors({
                        items: data.items,
                        total: data.total,
                    } as ValidationErrorsInterface);

                    if (data.total > 0) {
                        this.toastrService.warning('Foram encontrados erros na importação!', '', {
                            closeButton: true,
                        });
                    }

                    this.importWizardStorage.setNextStep();
                },
                error: (error) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao buscar erros de validação:', error);
                },
            });

        this._subs.add(sub);
    }
}